import { Box, FormControl, FormControlLabel, Radio, RadioGroup, Stack, Typography } from "@mui/material"
import { Bound } from "lib/@components/binding/Bound"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useRefresh } from "lib/@hooks/useRefresh"
import { useSave } from "lib/@hooks/use-save"
import { SaveIndicator } from "lib/@components/save-indicator/save-indicator"
import { AuthoringHeaderBarRight } from "authoring/routes/@layouts/authoring-app-layout"
import { updateProjectGroup } from "../controller/update-project-group"
import { useParams } from "react-router"
import { useDeferredValue, useEffect, useMemo, useRef, useState } from "react"
import { AmendsBoundaryEditor } from "authoring/routes/project/amends/components/amends-boundary-editor"
import { AmendsBoundary } from "authoring/routes/project/amends/components/amends-boundary"
import { AudienceAwareAmendsEditor } from "authoring/routes/project/amends/components/audience-aware-amends-editor"
import { useGroupAmends } from "../lib/use-group-amends"
import { LEADING_SUMMARY_AMENDS_KEY } from "../../amends/lib/constants/amends-keys"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { useChipExpander } from "authoring/routes/macro/hooks/use-chip-expander"
import { ScheduleMacroAutocomplete } from "authoring/routes/project/schedule-editor/schedule-macro-autocomplete"
import { stripChipHtml } from "authoring/routes/macro/lib/macro-chip-utils"
import { useEnrichedMacroMap } from "authoring/routes/macro/hooks/use-enriched-macro-map"
import { extractMacroTokensFromString } from "authoring/routes/macro/lib/macro-token-utils"

const SAVE_ID = "leading-summary-group-editor"
const SUMMARY_CONTENT_FIELDS = ["content", "internalContent", "externalContent"]

export function LeadingSummaryGroupEditor() {
    const { projectId, groupId } = useParams()
    const { projectGroup, groupLoading, savingRef } = useBoundContext()
    useEffect(resetSavingAfterLoad, [groupLoading, savingRef])
    const [leadingSummary, setLeadingSummary] = useState(null)
    const [overwriteExisting, setOverwriteExisting] = useState(false)
    const hydratedLeadingSummaryRef = useRef("")
    const [saveReady, setSaveReady] = useState(false)
    const isReadOnly = Boolean(projectGroup?.readOnly)
    const canEdit = !isReadOnly
    const deferredSummaryText = useDeferredValue(buildSummaryText(leadingSummary))
    const tokenIds = useMemo(collectLeadingSummaryTokenIds, [deferredSummaryText, projectGroup?.leadingSummary])
    const { macros: allMacros, macroMap, loading: macrosLoading } = useEnrichedMacroMap({ tokenIds })
    const { handleChipClick, chipExpanderPopover } = useChipExpander({ macroMap })

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(syncLeadingSummaryFromGroup, [leadingSummary, projectGroup, groupLoading, macrosLoading, saveReady])

    const onChange = useSave({ save: saveGroup, target: leadingSummary, id: SAVE_ID, wait: 1000 }, [projectId, groupId])
    const refresh = useRefresh()
    const { amends, saveAmend, deleteAmend } = useGroupAmends({ projectId, groupId })

    if (!leadingSummary) return <LoadingScreen />

    return (
        <Stack spacing={2}>
            <SaveIndicator id={SAVE_ID} slot={AuthoringHeaderBarRight} />
            <AmendsBoundary amends={amends} onSaveAmend={saveAmend} onDeleteAmend={deleteAmend}>
                <Box onClickCapture={handleChipClick}>
                    <Bound
                        target={leadingSummary}
                        onChange={handleChange}
                        refresh={refresh}
                        editMode={canEdit}
                        macroMap={macroMap}
                    >
                        <Typography variant="subtitle2">Leading Summary</Typography>
                        <Box data-cy="leading-summary-editor">
                            <AudienceAwareAmendsEditor
                                macroMap={macroMap}
                                editMode={canEdit}
                                canEdit={canEdit}
                                noteData={{ ...leadingSummary }}
                                onChange={handleSummaryChange}
                                hideActions
                                required={false}
                                autoFocusOnOpen={false}
                            />
                        </Box>
                        <AmendsBoundaryEditor amendsKey={LEADING_SUMMARY_AMENDS_KEY} data-cy="leading-summary-amends" />
                    </Bound>
                </Box>
                <ScheduleMacroAutocomplete macros={allMacros} enabled={true} isPreviewMode />
            </AmendsBoundary>
            <FormControl data-cy="leading-summary-overwrite-control">
                <Typography variant="subtitle2" sx={{ color: "text.secondary", mb: 1 }}>
                    Existing leading summaries
                </Typography>
                <RadioGroup value={overwriteExisting ? "overwrite" : "skip"} onChange={handleOverwriteChange}>
                    <FormControlLabel
                        value="skip"
                        control={<Radio disabled={isReadOnly} />}
                        label="Skip schedules that already have a leading summary"
                        data-cy="leading-summary-skip-radio"
                    />
                    <FormControlLabel
                        value="overwrite"
                        control={<Radio disabled={isReadOnly} />}
                        label="Overwrite existing leading summaries"
                        data-cy="leading-summary-overwrite-radio"
                    />
                </RadioGroup>
            </FormControl>
            {chipExpanderPopover}
        </Stack>
    )

    function syncLeadingSummaryFromGroup() {
        if (groupLoading || macrosLoading || !projectGroup) return
        const raw = projectGroup?.leadingSummary ?? { content: "" }
        const nextHydratedKey = buildLeadingSummaryHydratedKey(raw)

        if (!saveReady) {
            setLeadingSummary({ ...raw })
            hydratedLeadingSummaryRef.current = nextHydratedKey
            setOverwriteExisting(Boolean(projectGroup?.overwriteExisting))
            setSaveReady(true)
            return
        }

        setOverwriteExisting(Boolean(projectGroup?.overwriteExisting))
        if (nextHydratedKey === hydratedLeadingSummaryRef.current) return

        const currentNormalizedKey = buildLeadingSummaryHydratedKey(leadingSummary)
        if (currentNormalizedKey !== hydratedLeadingSummaryRef.current && currentNormalizedKey !== nextHydratedKey)
            return

        setLeadingSummary({ ...raw })
        hydratedLeadingSummaryRef.current = nextHydratedKey
    }

    function handleChange() {
        refresh()
        if (saveReady) onChange()
    }

    function handleSummaryChange(payload) {
        Object.assign(leadingSummary, payload)
        handleChange()
    }

    async function handleOverwriteChange({ target }) {
        const newValue = target?.value === "overwrite"
        setOverwriteExisting(newValue)
        if (!saveReady) return
        try {
            await triggerOverwriteSave(newValue)
        } catch (error) {
            setOverwriteExisting(!newValue)
            console.error(error)
        }
    }

    async function triggerOverwriteSave(overwrite) {
        if (!projectId || !groupId) return true
        savingRef.current = true
        const saved = await updateProjectGroup({
            projectId,
            group: { id: groupId, overwriteExisting: overwrite },
        })
        if (!saved) throw new Error("Failed to save overwrite setting")
        return true
    }

    function resetSavingAfterLoad() {
        if (!groupLoading && savingRef.current) {
            savingRef.current = false
        }
    }

    async function saveGroup(leadingSummary) {
        if (!projectId || !groupId) return true
        savingRef.current = true
        const saved = await updateProjectGroup({
            projectId,
            group: { id: groupId, leadingSummary: stripSummaryChips(leadingSummary) },
        })
        if (!saved) throw new Error("Failed to save leading summary")
        return true
    }

    function collectLeadingSummaryTokenIds() {
        const values = [deferredSummaryText, buildSummaryText(projectGroup?.leadingSummary)]
        const tokens = new Set()
        for (const value of values) {
            for (const token of extractMacroTokensFromString(value)) {
                tokens.add(token)
            }
        }
        return [...tokens]
    }

    function stripSummaryChips(summary = {}) {
        const output = { ...summary }
        for (const field of SUMMARY_CONTENT_FIELDS) {
            if (typeof output[field] === "string") output[field] = stripChipHtml(output[field])
        }
        return output
    }

    function buildLeadingSummaryHydratedKey(value = {}) {
        return JSON.stringify(
            Object.fromEntries(SUMMARY_CONTENT_FIELDS.map((field) => [field, stripChipHtml(value?.[field] ?? "")]))
        )
    }
}

function buildSummaryText(summary) {
    return SUMMARY_CONTENT_FIELDS.map((field) => summary?.[field] ?? "").join("\n")
}
